import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

export const AthleteStaticWarmupRoutineExercise = () => {
  const { athlete } = useAthleteAuthContext();
  const { phase_id, warmup_routine_id, template_id } = useParams();
  const [exercises, setExercises] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [phase, setPhase] = useState("");
  const [modalOpen, setModalOpen] = useState(false); // State for modal visibility
  const [selectedVideo, setSelectedVideo] = useState(""); // State to store the selected video URL
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExercisesData = async () => {
      try {
        const response = await fetch(
          API_URL.GET_STATIC_WARMUP_ROUTINE_PHASE_EXERCISES + phase_id,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${athlete.token}`,
            },
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData?.error || "Failed to fetch exercises data");
          return;
        }

        const data = await response.json();
        setExercises(data.exercises);
        setPhase(data.phase);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExercisesData();
  }, [athlete.token, athlete.email, phase_id]);

  const changeDate = (days) => {
    setSelectedDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + days);
      return newDate;
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };

  const goBack = () => {
    navigate(-1); // Navigate to the previous page
  };

  const openVideoModal = (videoUrl) => {
    setSelectedVideo(videoUrl);
    setModalOpen(true); // Open the modal when an exercise with a video is clicked
  };

  const closeModal = () => {
    setModalOpen(false); // Close the modal
    setSelectedVideo(""); // Clear the selected video URL
  };

  const handleStaticSpeedClick = () => {
    navigate(
      "/athlete/static-speed-workout/" + template_id + "/" + warmup_routine_id,
    );
  };

  return (
    <AthleteLayout>
      <div className="bg-white">
        <AthleteHeader />
      </div>

      <div className="pb-3 flex flex-row justify-center items-center gap-10 bg-white">
        <ArrowL onClick={() => changeDate(-1)} />
        <p className="text-black text-xl font-normal">
          {formatDate(selectedDate)}
        </p>
        <ArrowG onClick={() => changeDate(1)} />
      </div>

      <div className="w-full max-w-2xl mx-auto h-full">
        <div className="py-3">
          
          <div className="px-5 mb-4">
            <div className="bg-[#f5f4f4] rounded-2xl flex justify-between gap-8 px-4 py-4">
              <button className="text-black text-sm xs:text-base">
                Warmup 
              </button>
              <button
                onClick={() => handleStaticSpeedClick()}
                className="text-lightSmock text-sm xs:text-base"
              >
                 Workout
              </button>
            </div>
          </div>

          <div className="w-full text-center relative">
            <span className="absolute left-5 top-1/2 -translate-y-1/2">
              <ArrowLWhite onClick={goBack} />
            </span>
            <p key={1} className="text-black text-base font-normal inline-flex">
              {phase}
            </p>
          </div>

          {isLoading ? (
            <p className="text-center text-black mt-4">Loading...</p>
          ) : error ? (
            <p className="text-center text-red-500 mt-4">{error}</p>
          ) : exercises.length > 0 ? (
            <div className="space-y-[15px] h-full overflow-hidden overflow-y-auto mt-[22px] px-5">
              {exercises.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-row justify-between items-center pb-[15px] border-b border-black/30"
                  onClick={() =>
                    item.video_url && openVideoModal(item.video_url)
                  } // Open modal on click
                >
                  <div className="flex flex-col gap-2">
                    <p className="text-black text-base font-medium leading-[95%]">
                      {item.title}
                    </p>
                    <span className="text-black text-[13px] font-light">
                      {item.description}
                    </span>
                  </div>
                  {item.video_url && (
                    <img
                      src={item.video_thumbnail}
                      alt="exercise video thumbnail"
                      className="w-10 h-10  cursor-pointer hover:opacity-80 transition-opacity duration-300"
                      onClick={() => openVideoModal(item.video_url)} // Trigger the modal on click
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-black mt-4">No exercises found.</p>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-75 z-50"
          onClick={closeModal} // Close modal if user clicks outside the modal
        >
          <div
            className="relative bg-black w-full max-w-5xl rounded-lg p-5"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
          >
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold"
              onClick={closeModal} // Close modal on button click
            >
              X
            </button>
            <div
              className="relative w-full h-0"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                width="100%"
                height="100%"
                src={selectedVideo}
                title="Exercise Video"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0"
              />
            </div>
          </div>
        </div>
      )}
    </AthleteLayout>
  );
};

const ArrowLWhite = ({ onClick }) => {
  return (
    <svg
      onClick={onClick}
      className="hover:opacity-50 rotate-180 cursor-pointer"
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
    >
      <path
        d="M1 13L7 7L0.999999 1"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const ArrowG = ({ onClick }) => {
  return (
    <svg
      onClick={onClick}
      className="hover:opacity-50 cursor-pointer"
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
    >
      <path
        d="M1 13L7 7L0.999999 1"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const ArrowL = ({ onClick }) => {
  return (
    <svg
      onClick={onClick}
      className="hover:opacity-50 rotate-180 cursor-pointer"
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
    >
      <path
        d="M1 13L7 7L0.999999 1"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
